import Reveal from './ui/Reveal';
import { LogoMark } from './ui/Logo';
import Icon, { GithubIcon, LinkedinIcon } from './ui/Icon';
import SectionHeading from './ui/SectionHeading';
import { principles, site } from '../data/content';

const About = () => (
  <section id="about" className="section border-t border-white/[0.06]" aria-labelledby="about-title">
    <div className="container-page">
      <div className="grid gap-12 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16">
        <div>
          <SectionHeading
            id="about-title"
            eyebrow="About"
            title="A web and AI development business focused on better digital experiences"
          />
          <Reveal className="mt-6 max-w-2xl space-y-4 text-base leading-relaxed text-muted sm:text-lg">
            <p>
              KBL Web Solutions is a web development business based in the San Francisco Bay Area, founded and run by{' '}
              <span className="font-medium text-fg">{site.owner}</span>. I help businesses build websites people trust,
              applications that fit the way they work, and AI features that save time.
            </p>
            <p>
              Much of my work serves multilingual communities across Northern California, so I care about clarity,
              accessibility and getting the details right, from the first call to launch and beyond.
            </p>
          </Reveal>

          <ul className="mt-10 grid gap-4 sm:grid-cols-3">
            {principles.map((item, i) => (
              <Reveal as="li" key={item.title} delay={i * 80}>
                <div className="h-full border-l border-indigo-300/30 pl-4">
                  <h3 className="text-[15px] font-semibold text-fg">{item.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted">{item.text}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>

        <Reveal delay={120}>
          <aside className="card p-6 sm:p-8" aria-label={`About ${site.owner}`}>
            <div className="flex items-center gap-4">
              <LogoMark className="h-14 w-14 rounded-2xl" />
              <div>
                <p className="text-lg font-semibold tracking-tight text-fg">{site.owner}</p>
                <p className="text-sm text-muted">Founder &amp; developer, KBL Web Solutions</p>
              </div>
            </div>

            <dl className="mt-7 space-y-4 border-t border-white/[0.07] pt-6 text-sm">
              <div className="flex gap-3">
                <dt className="w-24 shrink-0 font-mono text-xs uppercase tracking-wider text-subtle">Based in</dt>
                <dd className="text-fg">{site.location}</dd>
              </div>
              <div className="flex gap-3">
                <dt className="w-24 shrink-0 font-mono text-xs uppercase tracking-wider text-subtle">Languages</dt>
                <dd className="text-fg">English, Farsi, Pashto</dd>
              </div>
              <div className="flex gap-3">
                <dt className="w-24 shrink-0 font-mono text-xs uppercase tracking-wider text-subtle">Focus</dt>
                <dd className="text-fg">React &amp; Next.js front ends, custom web apps, AI integration</dd>
              </div>
            </dl>

            <div className="mt-7 flex flex-wrap gap-3 border-t border-white/[0.07] pt-6">
              <a
                href={site.github}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-10 items-center gap-2 rounded-full border border-white/12 px-4 text-sm font-medium text-muted transition-colors hover:border-white/30 hover:text-fg"
              >
                <GithubIcon className="h-4 w-4" />
                GitHub
                <span className="sr-only">(opens in a new tab)</span>
              </a>
              <a
                href={site.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-10 items-center gap-2 rounded-full border border-white/12 px-4 text-sm font-medium text-muted transition-colors hover:border-white/30 hover:text-fg"
              >
                <LinkedinIcon className="h-4 w-4" />
                LinkedIn
                <span className="sr-only">(opens in a new tab)</span>
              </a>
              <a
                href={`mailto:${site.email}`}
                className="inline-flex h-10 items-center gap-2 rounded-full border border-white/12 px-4 text-sm font-medium text-muted transition-colors hover:border-white/30 hover:text-fg"
              >
                <Icon name="Mail" className="h-4 w-4" />
                Email
              </a>
            </div>
          </aside>
        </Reveal>
      </div>
    </div>
  </section>
);

export default About;
